import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { mediatorsAPI } from '../services/api';
import {
  ArrowLeft, Plus, Trash2, Network as NetworkIcon, Copy, Loader2, Link2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCHF = (v) => `CHF ${(v || 0).toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

export const AdminMediators = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', commissionRate: 25
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await mediatorsAPI.adminList();
      if (res?.success) setList(res.mediators || []);
    } catch (e) {
      toast.error(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await mediatorsAPI.adminCreate({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        commissionRate: Number(form.commissionRate)
      });
      toast.success('Vermittler angelegt');
      setIsOpen(false);
      setForm({ firstName: '', lastName: '', email: '', password: '', commissionRate: 25 });
      load();
    } catch (err) {
      toast.error(err.message || 'Konnte nicht angelegt werden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Vermittler "${m.firstName} ${m.lastName}" löschen? Untervermittler und zugeordnete Investoren werden abgekoppelt (nicht gelöscht).`)) return;
    try {
      await mediatorsAPI.adminRemove(m.id);
      toast.success('Vermittler gelöscht');
      load();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
  };

  const handleApprove = async (m) => {
    const rateStr = window.prompt(
      `Vermittler "${m.firstName} ${m.lastName}" freischalten.\n\nBitte Provisionssatz (in %) eingeben:`,
      '20'
    );
    if (rateStr === null) return;
    const rate = Number(rateStr);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error('Provisionssatz muss zwischen 0 und 100 liegen');
      return;
    }
    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
      const r = await fetch(`${API_BASE}/api/mediators/admin/${m.id}/approve`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commissionRate: rate }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Freischaltung fehlgeschlagen');
      toast.success('Vermittler freigeschaltet');
      load();
    } catch (err) {
      toast.error(err.message || 'Freischaltung fehlgeschlagen');
    }
  };

  const copyLink = (code) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://loewenherz.ag';
    const url = `${origin}/?ref=${code}`;
    navigator.clipboard?.writeText(url);
    toast.success('Referral-Link kopiert');
  };

  const totalProvision = list.reduce((s, m) => s + (m.directProvision || 0), 0);
  const totalVolume = list.reduce((s, m) => s + (m.directInvestmentVolume || 0), 0);

  // Build top-level set
  const topLevel = list.filter((m) => !m.parentMediatorId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-[#C4A55A] hover:text-[#D4B56A] mb-2"
              data-testid="admin-mediators-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <NetworkIcon className="h-9 w-9 text-[#C4A55A]" />
              Vermittler
            </h1>
            <p className="text-gray-400 mt-1">
              Hierarchische Vermittler-Struktur · Provision aus dem Agio
            </p>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="admin-mediators-create-open-btn"
                className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Top-Vermittler anlegen
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">Neuer Vermittler</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Top-Level-Vermittler. Kann später eigene Untervermittler anlegen.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Vorname</Label>
                    <Input
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="admin-mediators-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nachname</Label>
                    <Input
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="admin-mediators-lastname-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">E-Mail</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    required
                    data-testid="admin-mediators-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Passwort (min. 8 Zeichen)</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    required
                    minLength={8}
                    data-testid="admin-mediators-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Provisionssatz (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={form.commissionRate}
                    onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    required
                    data-testid="admin-mediators-rate-input"
                  />
                  <p className="text-xs text-gray-500">
                    Provision als % des Investment-Betrags eines direkt geworbenen Investors
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                  data-testid="admin-mediators-create-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lege an...</>
                  ) : 'Vermittler anlegen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Tile label="Vermittler gesamt" value={list.length} />
          <Tile label="Davon Top-Level" value={topLevel.length} />
          <Tile label="Vermitteltes Volumen" value={fmtCHF(totalVolume)} />
          <Tile label="Direkte Provision" value={fmtCHF(totalProvision)} accent />
        </div>

        {loading ? (
          <p className="text-gray-400" data-testid="admin-mediators-loading">Lädt...</p>
        ) : list.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardContent className="py-12 text-center text-gray-400" data-testid="admin-mediators-empty">
              Noch keine Vermittler angelegt. Klicken Sie auf „Top-Vermittler anlegen", um zu starten.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-mediators-list">
            {list.map((m) => (
              <Card
                key={m.id}
                data-testid={`admin-mediators-card-${m.id}`}
                className={`bg-[#1a1a1a] ${
                  !m.parentMediatorId ? 'border-[#C4A55A]/40' : 'border-[#C4A55A]/20'
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-white text-base truncate">
                        {m.firstName} {m.lastName}
                      </CardTitle>
                      <CardDescription className="text-gray-400 truncate">
                        {m.email}
                      </CardDescription>
                    </div>
                    {m.pendingApproval ? (
                      <span className="px-2 py-0.5 text-[10px] uppercase rounded bg-amber-500/20 text-amber-300 shrink-0" data-testid={`admin-mediator-pending-${m.id}`}>
                        Wartet
                      </span>
                    ) : !m.parentMediatorId ? (
                      <span className="px-2 py-0.5 text-[10px] uppercase rounded bg-[#C4A55A]/20 text-[#C4A55A] shrink-0">
                        Top
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] uppercase rounded bg-emerald-500/15 text-emerald-400 shrink-0">
                        Sub
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <button
                    onClick={() => copyLink(m.referralCode)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-black/40 border border-[#C4A55A]/20 hover:border-[#C4A55A]/50 text-sm transition-colors"
                    data-testid={`admin-mediators-copy-link-${m.id}`}
                  >
                    <span className="flex items-center gap-2 text-[#C4A55A] font-mono">
                      <Link2 className="h-4 w-4" />
                      {m.referralCode}
                    </span>
                    <Copy className="h-4 w-4 text-gray-400" />
                  </button>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <Stat label="Investoren" value={m.directInvestorCount} />
                    <Stat label="Volumen" value={fmtCHF(m.directInvestmentVolume)} />
                    <Stat label="Satz" value={`${m.commissionRate.toFixed(1)}%`} />
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-[#C4A55A]/10">
                    <span className="text-xs text-gray-500">{fmtDate(m.createdAt)}</span>
                    <div className="flex items-center gap-2">
                      {!m.pendingApproval && (
                        <span className="text-sm text-[#C4A55A] font-semibold">
                          {fmtCHF(m.directProvision)}
                        </span>
                      )}
                      {m.pendingApproval && (
                        <Button
                          size="sm"
                          className="bg-emerald-500/20 hover:bg-emerald-500 text-emerald-200 hover:text-white"
                          onClick={() => handleApprove(m)}
                          data-testid={`admin-mediators-approve-btn-${m.id}`}
                          title="Vermittler freischalten"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Freischalten
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                        onClick={() => handleDelete(m)}
                        data-testid={`admin-mediators-delete-btn-${m.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Tile = ({ label, value, accent }) => (
  <div className={`rounded-xl p-5 border ${
    accent ? 'bg-gradient-to-br from-[#C4A55A]/15 to-[#1a1a1a] border-[#C4A55A]/40' : 'bg-[#1a1a1a] border-[#C4A55A]/20'
  }`}>
    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">{label}</div>
    <div className="text-2xl md:text-3xl font-bold text-white">{value}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="text-center">
    <div className="text-gray-500 text-[10px] uppercase">{label}</div>
    <div className="text-white font-semibold mt-0.5">{value}</div>
  </div>
);
